import type { BookedSpot } from './bookedSpotNames';
import { nonEmptyTrimmed } from './spotName';

export const RESERVE_SPOT_API_VERSION = 'v3.0';

/** Error key `getReserveSpotError` falls back to when the caller passes none. */
export const DEFAULT_RESERVE_SPOT_ERROR_KEY = 'BOOK_SPOT_ERROR';

export const ReserveSpotErrorCode = {
  SPOT_ALREADY_TAKEN: 'SPOT_ALREADY_TAKEN',
  SPOT_NOT_BOOKABLE: 'SPOT_NOT_BOOKABLE'
} as const;

export type ReserveSpotErrorCode = (typeof ReserveSpotErrorCode)[keyof typeof ReserveSpotErrorCode];

/**
 * `message_code` a host app stamps on the error it raises when `resolveFailedReserveSpotNames`
 * reports a partial/total friends bulk-reserve failure, so the render path can tell that case apart
 * from a generic reserve error without depending on how the error was dispatched.
 */
export const FRIENDS_SPOTS_UNAVAILABLE_CODE = 'SPOTS_NO_LONGER_AVAILABLE';

export type ReserveSpotError = {
  code?: string | undefined;
  message?: string | undefined;
  errors: string[];
  messagesData: Record<string, unknown>;
};

export type ReserveSpotWireErrorBody = {
  code?: string;
  message?: string;
  errors?: string[];
  message_data?: Record<string, unknown>;
};

export type ReserveSpotWireBody = {
  old_spot_name: string;
};

/** Bulk reserve body (friends mode): one entry per slot, `guest === 0` is the member. */
export type FriendsSpotsReserveWireBody = {
  spots: BookedSpot[];
};

export type ReserveSpotResultError = {
  message?: string;
  code?: string;
};

/**
 * One outcome in the friends bulk-reserve `results` array. A `success: true` entry carries `status`
 * (`BOOKED`) and `auto_reserved`; a `success: false` entry carries a spot-level `error`.
 */
export type ReserveSpotResult =
  | { success: true; spot_name: string; guest: number; status?: string; auto_reserved?: boolean }
  | { success: false; spot_name: string; guest: number; error?: ReserveSpotResultError };

/**
 * The slice of the bulk-reserve response {@link resolveFailedReserveSpotNames} reads. Hosts that
 * only feed that helper can type their fetch with this instead of the full
 * {@link ReserveSpotsResponse}.
 */
export type ReserveSpotsResultsPayload = {
  results?: ReserveSpotResult[];
};

/** Friends bulk-reserve 200 response: one `results` entry per requested spot, in request order. */
export type ReserveSpotsResponse = {
  booking_id: string;
  time_slot_id: string;
  location_id: string;
  member_id: string;
  results: ReserveSpotResult[];
  modified: string;
  modified_by_user_id: string;
};

export function buildReserveSpotUrl(baseUrl: string, bookingId: string, spotName: string): string {
  const base = baseUrl.replace(/\/$/, '');
  return `${base}/${RESERVE_SPOT_API_VERSION}/bookings/${bookingId}/spots/${encodeURIComponent(
    spotName
  )}/reserve`;
}

export function buildReserveSpotBody(oldSpotName?: string | null): ReserveSpotWireBody | undefined {
  const resolved = nonEmptyTrimmed(oldSpotName);
  return resolved ? { old_spot_name: resolved } : undefined;
}

/** Bulk reserve (friends mode): spot names live in the body, not the path. */
export function buildFriendsSpotsReserveUrl(baseUrl: string, bookingId: string): string {
  const base = baseUrl.replace(/\/$/, '');
  return `${base}/${RESERVE_SPOT_API_VERSION}/bookings/${bookingId}/spots/reserve`;
}

type ReserveSpotErrorLike = Partial<ReserveSpotError> & ReserveSpotWireErrorBody;

function isReserveSpotError(value: unknown): value is ReserveSpotErrorLike {
  return typeof value === 'object' && value !== null && ('errors' in value || 'code' in value);
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export function normalizeReserveErrorBody(body: ReserveSpotWireErrorBody): ReserveSpotError {
  return {
    code: typeof body.code === 'string' ? body.code : undefined,
    message: typeof body.message === 'string' ? body.message : undefined,
    errors: Array.isArray(body.errors) ? body.errors : [],
    messagesData: asRecord(body.message_data)
  };
}

export function getReserveSpotError(
  error: unknown,
  fallbackErrorKey: string = DEFAULT_RESERVE_SPOT_ERROR_KEY
): ReserveSpotError {
  if (!isReserveSpotError(error)) {
    return { errors: [fallbackErrorKey], messagesData: {} };
  }
  const errors =
    Array.isArray(error.errors) && error.errors.length > 0 ? error.errors : [fallbackErrorKey];
  return {
    code: typeof error.code === 'string' ? error.code : undefined,
    message: typeof error.message === 'string' ? error.message : undefined,
    errors,
    messagesData: asRecord(error.messagesData ?? error.message_data)
  };
}

export function isSpotAlreadyTaken(error: unknown): boolean {
  return isReserveSpotError(error) && error.code === ReserveSpotErrorCode.SPOT_ALREADY_TAKEN;
}

export function isSpotNotBookable(error: unknown): boolean {
  return isReserveSpotError(error) && error.code === ReserveSpotErrorCode.SPOT_NOT_BOOKABLE;
}

export function requiresSpotReselection(error: unknown): boolean {
  return isSpotAlreadyTaken(error) || isSpotNotBookable(error);
}

export function isFriendsSpotsUnavailable(
  error: { message_code?: string } | null | undefined
): boolean {
  return error?.message_code === FRIENDS_SPOTS_UNAVAILABLE_CODE;
}

/**
 * A `results` entry as it arrives off the wire, before any field is trusted. Every field is optional
 * because {@link resolveFailedReserveSpotNames} has to read an entry that omits `success`, which the
 * {@link ReserveSpotResult} union does not admit.
 */
type ReserveSpotResultLike = {
  success?: boolean;
  spot_name?: string;
  guest?: number;
  error?: ReserveSpotResultError;
};

function isReserveSpotResultLike(value: unknown): value is ReserveSpotResultLike {
  return typeof value === 'object' && value !== null;
}

/** Per-spot counterpart of {@link requiresSpotReselection}, for the friends bulk-reserve `results` error shape. */
function resultErrorRequiresReselection(error: ReserveSpotResultError | undefined): boolean {
  return (
    error?.code === ReserveSpotErrorCode.SPOT_ALREADY_TAKEN ||
    error?.code === ReserveSpotErrorCode.SPOT_NOT_BOOKABLE
  );
}

/**
 * The friends bulk-reserve endpoint returns HTTP 200 even when individual spots fail. Returns the
 * `spot_name` of every result that failed with a reselect-required code (taken / not bookable) so the
 * caller can re-highlight those slots for re-picking. Tolerant of a missing or malformed `results`
 * array — returns an empty array when nothing needs reselection.
 */
export function resolveFailedReserveSpotNames(
  response: { results?: unknown } | null | undefined
): string[] {
  const results = Array.isArray(response?.results) ? (response.results as unknown[]) : [];
  const names: string[] = [];
  for (const result of results) {
    if (!isReserveSpotResultLike(result)) continue;
    const { success, error, spot_name } = result;
    // Skip only explicit successes: a wire entry that omits `success` but carries a reselect
    // error still counts as failed.
    if (success === true) continue;
    if (!resultErrorRequiresReselection(error)) continue;
    const name = nonEmptyTrimmed(spot_name);
    if (name) names.push(name);
  }
  return names;
}
